import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ChannelCreateInput, ChannelDto, ChannelUpdateInput } from '@telega/shared';
import { request } from './client';

export const channelKeys = { all: ['channels'] as const };

export const channelsQuery = {
  queryKey: channelKeys.all,
  queryFn: () => request<{ items: ChannelDto[] }>('/channels').then((r) => r.items),
};

export function useChannels(options: { refetchInterval?: number | false } = {}) {
  return useQuery({ ...channelsQuery, ...options });
}

/** PATCH with an optimistic update, so the pin switch reacts instantly. */
export function useUpdateChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: ChannelUpdateInput & { id: string }) =>
      request<ChannelDto>(`/channels/${id}`, { method: 'PATCH', body }),
    onMutate: async ({ id, ...patch }) => {
      await qc.cancelQueries({ queryKey: channelKeys.all });
      const previous = qc.getQueryData<ChannelDto[]>(channelKeys.all);
      qc.setQueryData<ChannelDto[]>(channelKeys.all, (list) =>
        list?.map((c) => (c.id === id ? { ...c, ...patch } : c)),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) qc.setQueryData(channelKeys.all, context.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: channelKeys.all }),
  });
}

export function useCreateChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ChannelCreateInput) =>
      request<ChannelDto>('/channels', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: channelKeys.all }),
  });
}

export function useDeleteChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => request<void>(`/channels/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: channelKeys.all }),
  });
}
